import { toMonthKey } from "@shared";

import {
  CounselorDutyType,
  DayOfWeek,
  DutyColumnKind,
  DutyScheduleCounselorOptionResponse,
  DutyScheduleSlotResponse,
  DutyScheduleWeekdayPreferenceResponse,
} from "@/api";

export interface DutyScheduleMonth {
  key: string;
  year: number;
  month: number;
  workingDays: Date[];
}

export function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function cellKey(date: Date, columnId: string): string {
  return `${dateKey(date)}|${columnId}`;
}

// Grouped from the stored working days rather than walking the period, so a holiday imported later
// cannot remove a row that was calculated as a working day.
export function groupWorkingDaysByMonth(workingDayDates: Date[]): DutyScheduleMonth[] {
  const byMonth = new Map<string, DutyScheduleMonth>();

  for (const date of [...workingDayDates].sort((left, right) => left.getTime() - right.getTime())) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const key = toMonthKey(year, month);

    const existing = byMonth.get(key);
    if (existing) {
      existing.workingDays.push(date);
    } else {
      byMonth.set(key, { key, year, month, workingDays: [date] });
    }
  }

  return [...byMonth.values()];
}

export interface DutyScheduleCell {
  // null renders as "—": the duty exists, nobody is on it.
  counselorId: string | null;
  // The occupant's current counselor code, shown in the cell. Display only: a counselor code can be changed, so cells
  // are compared and filtered through counselorId.
  userCode: string | null;
  matchesPreference: boolean;
}

// A missing key means the duty schedule never had that cell — for a court column, that it was not in
// session that day. That is what tells a shaded cell apart from an unstaffed duty.
export type DutyScheduleCellIndex = Map<string, DutyScheduleCell>;

export function indexSlots(slots: DutyScheduleSlotResponse[]): DutyScheduleCellIndex {
  const byCell: DutyScheduleCellIndex = new Map();

  for (const slot of slots) {
    byCell.set(cellKey(slot.date, slot.columnId), {
      counselorId: slot.counselorId,
      userCode: slot.counselorUserCode,
      matchesPreference: slot.matchesPreference,
    });
  }

  return byCell;
}

// Reads the SAVED slots, not the pending edits: the filter drives which rows the table shows, so
// resolving it live would make a row vanish the moment its last duty is edited away, taking the
// change out of view.
export function datesWithCounselor(slots: DutyScheduleSlotResponse[], counselorIds: string[]): Set<string> {
  const dates = new Set<string>();

  for (const slot of slots) {
    if (slot.counselorId != null && counselorIds.includes(slot.counselorId)) {
      dates.add(dateKey(slot.date));
    }
  }

  return dates;
}

// The counselor code of every counselor holding a duty in the plan, keyed by counselor id. Taken from the slots
// rather than the staff list, so somebody who has since left still has a label.
export function userCodesByCounselorId(slots: DutyScheduleSlotResponse[]): Map<string, string> {
  const userCodes = new Map<string, string>();

  for (const slot of slots) {
    if (slot.counselorId != null && slot.counselorUserCode != null) {
      userCodes.set(slot.counselorId, slot.counselorUserCode);
    }
  }

  return userCodes;
}

// getDay() is 0 = Sunday, matching the API enum order.
const WEEKDAY_BY_INDEX: DayOfWeek[] = [
  DayOfWeek.Sunday,
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
];

export function weekdayOf(date: Date): DayOfWeek {
  // getDay() is always in range; the fallback only satisfies the index signature.
  return WEEKDAY_BY_INDEX[date.getDay()] ?? DayOfWeek.Sunday;
}

// AW1 and AW2 share one checkbox on the "Präferenzen für Dienstplan" screen, which is why they answer alike.
function matchesKind(preference: DutyScheduleWeekdayPreferenceResponse, kind: DutyColumnKind): boolean {
  switch (kind) {
    case DutyColumnKind.Aw1:
    case DutyColumnKind.Aw2:
      return preference.aw12;
    case DutyColumnKind.Aw3:
      return preference.aw3;
    case DutyColumnKind.Court:
      return preference.court;
    default:
      return false;
  }
}

function coversKind(dutyType: CounselorDutyType, kind: DutyColumnKind): boolean {
  switch (dutyType) {
    case CounselorDutyType.Aw12:
      return kind === DutyColumnKind.Aw1 || kind === DutyColumnKind.Aw2;
    case CounselorDutyType.Aw3:
      return kind === DutyColumnKind.Aw3;
    case CounselorDutyType.Court:
      return kind === DutyColumnKind.Court;
    default:
      return false;
  }
}

function isBlocked(counselor: DutyScheduleCounselorOptionResponse, date: Date, kind: DutyColumnKind): boolean {
  const day = dateKey(date);

  return counselor.blockedPeriods.some(
    (period) => coversKind(period.dutyType, kind) && dateKey(period.from) <= day && day <= dateKey(period.to)
  );
}

// Mirrors the backend MatchesPreferenceFor — keep the two in step. A Sperrzeitraum always wins;
// somebody who has ticked no weekday and set no court preference is available on every weekday.
export function matchesCell(
  counselor: DutyScheduleCounselorOptionResponse,
  date: Date,
  kind: DutyColumnKind,
  courtId: string | null
): boolean {
  if (isBlocked(counselor, date, kind)) {
    return false;
  }

  if (!counselor.isConfigured) {
    return true;
  }

  const weekday = weekdayOf(date);
  const matchesWeekday = counselor.dutyPreferences.some(
    (preference) => preference.weekday === weekday && matchesKind(preference, kind)
  );

  return matchesWeekday && matchesCourt(counselor, kind, courtId);
}

// An empty preferred list means no preference between courts, so any of them counts.
function matchesCourt(
  counselor: DutyScheduleCounselorOptionResponse,
  kind: DutyColumnKind,
  courtId: string | null
): boolean {
  if (kind !== DutyColumnKind.Court || courtId == null) {
    return true;
  }

  if (counselor.excludedCourtIds.includes(courtId)) {
    return false;
  }

  return counselor.preferredCourtIds.length === 0 || counselor.preferredCourtIds.includes(courtId);
}

export function formatCounselorName(counselor: Pick<DutyScheduleCounselorOptionResponse, "firstName" | "lastName">) {
  return `${counselor.lastName}, ${counselor.firstName}`;
}

export interface DutyPreferenceLine {
  weekday: DayOfWeek;
  kinds: DutyColumnKind[];
}

// Aw1 stands for the shared AW1/AW2 checkbox, so the pair is listed once.
const PREFERENCE_KINDS: DutyColumnKind[] = [DutyColumnKind.Aw1, DutyColumnKind.Aw3, DutyColumnKind.Court];

// Kinds stay as enum values so the caller does the translating.
export function toDutyPreferenceLines(counselor: DutyScheduleCounselorOptionResponse): DutyPreferenceLine[] {
  return counselor.dutyPreferences
    .map((preference) => ({
      weekday: preference.weekday,
      kinds: PREFERENCE_KINDS.filter((kind) => matchesKind(preference, kind)),
    }))
    .filter((line) => line.kinds.length > 0);
}
